import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Options, Period, ReportType } from 'app/shared/report';

import { ReportPeriodConfiguration } from './configuration';
import { OneOffOrRecurringPeriod, OneOffPeriod, RecurringPeriod } from './report-period.model';

@Injectable()
export class ReportPeriodService {
    private recurringPeriods: RecurringPeriod[];
    private oneOffPeriods: OneOffPeriod[];

    constructor(
        private readonly http: HttpClient,
        private readonly reportPeriodConfiguration: ReportPeriodConfiguration,
    ) {}

    /**
     * Get list of recurring reporter options
     * @returns {Observable<RecurringPeriod>[]} : persist recurring reporter periods
     */
    getReporterRecurringPeriods(siteId: string): Observable<RecurringPeriod[]> {
        const url: string = this.reportPeriodConfiguration.apiPaths.getRecurringReportPeriods(siteId);
        return this.http.get<RecurringPeriod[]>(url);
    }

    /**
     * Get list of one-off reporter options
     * @returns {Observable<OneOffPeriod>[]} : persis one-off reporter periods
     */
    getReporterOneOffPeriods(siteId: string): Observable<OneOffPeriod[]> {
        const url: string = this.reportPeriodConfiguration.apiPaths.getOneOffReportPeriods(siteId);
        return this.http.get<OneOffPeriod[]>(url);
    }

    /**
     * Get list of available periods based on selected report type (recurring/one-off)
     * @param reportType the type to get available periods for
     */

    getPeriodsOnReportType(reportType: ReportType, siteId: string): Observable<any> {
        if (reportType === ReportType.OneOff) {
            return this.getReporterOneOffPeriods(siteId).pipe(
                map((result) => {
                    this.oneOffPeriods = result;
                    /**
                     * From api it's:
                     fromDate: "2021-08-01"
                     period: 13
                     textLabel: "CUSTOM"
                     toDate: "2021-08-02"
                     */
                    const periodList = this.setPeriodList(result);

                    /**
                     * NOW it's:
                     label: "REPORT_DIALOG.FREQUENCY.PERIOD.CUSTOM"
                     option: "custom"
                     */
                    return periodList;
                }),
            );
        }

        if (reportType === ReportType.Recurring) {
            return this.getReporterRecurringPeriods(siteId).pipe(
                map((result) => {
                    this.recurringPeriods = result;
                    const periodList = this.setPeriodList(result);
                    return periodList;
                }),
            );
        }
        return of([]);
    }

    private setPeriodList(periodList: OneOffOrRecurringPeriod[]) {
        return this.formatPeriodList(periodList);
    }

    private formatPeriodList(result: OneOffOrRecurringPeriod[]): Period[] {
        return result.map((listItem: OneOffOrRecurringPeriod) => ({
            label: `REPORT_DIALOG.FREQUENCY.PERIOD.${listItem.textLabel}`,
            option: Options[listItem.textLabel as keyof typeof Options],
        }));
    }

    /**
     * Gets the original object from the previously retrieved list of one off period options
     * @param selectedPeriod the period that is selected
     */
    getSelectedOneOffOption(selectedPeriod: Options): OneOffPeriod {
        if (!selectedPeriod) {
            return this.oneOffPeriods[0];
        }
        return this.oneOffPeriods?.find(
            (period) => Options[period.textLabel as keyof typeof Options] === selectedPeriod,
        );
    }

    /**
     * Gets the original object from the previously retrieved list of recurring period options
     * @param selectedPeriod the period that is selected
     */
    getSelectedRecurringOption(selectedPeriod: Options): RecurringPeriod {
        return this.recurringPeriods?.find(
            (period) => Options[period.textLabel as keyof typeof Options] === selectedPeriod,
        );
    }
}
